import React, { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronRight, MapPin, Phone } from "lucide-react";
import { siteData } from "../mock";
import { FASCIA_SPECIALTIES, COACHING_SPECIALTIES, CITIES, SERVICE_ZONES_TEXT } from "../data/specialties";
import { fasciaUrl, coachUrl, cityUrl } from "../data/urls";
import SEO from "../components/SEO";
import CTASection from "../components/CTASection";
import RelatedArticles from "../components/RelatedArticles";
import JsonLd from "../components/JsonLd";
import { breadcrumbSchema, serviceSchema } from "../data/schema";

export default function SpecialtyPage({ service, slug: slugProp }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const params = useParams();
  const slug = slugProp || params.slug;
  const list = service === "fasciatherapie" ? FASCIA_SPECIALTIES : COACHING_SPECIALTIES;
  const item = useMemo(() => list.find((s) => s.slug === slug), [list, slug]);
  const otherSpecialties = useMemo(
    () => (item ? list.filter((s) => s.slug !== slug).slice(0, 3) : []),
    [list, slug, item]
  );
  if (!item) return <Navigate to={service === "fasciatherapie" ? "/fasciathérapie-toulouse" : "/coach-domicile"} replace />;

  const parentPath = service === "fasciatherapie" ? "/fasciathérapie-toulouse" : "/coach-domicile";
  const parentLabel = service === "fasciatherapie" ? "Fasciathérapie" : "Coach sportif";

  const selfPath = service === "fasciatherapie" ? fasciaUrl(item.slug) : coachUrl(item.slug);
  const jsonLd = [
    serviceSchema({
      name: item.h1,
      serviceType: parentLabel,
      description: item.metaDescription,
      areaServed: CITIES.map((c) => c.short || c.name),
    }),
    breadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: parentLabel, path: parentPath },
      { name: item.keyword, path: selfPath },
    ]),
  ];

  return (
    <>
      <SEO
        title={item.metaTitle}
        description={item.metaDescription}
        keywords={item.keywords}
        image={item.image}
        type="article"
      />
      <JsonLd data={jsonLd} />

      {/* Breadcrumbs */}
      <div className="container-tight pt-6">
        <nav className="flex items-center gap-2 text-xs text-[#8a7d6a]" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-[#c76d4b]">Accueil</Link>
          <ChevronRight size={12} />
          <Link to={parentPath} className="hover:text-[#c76d4b]">{parentLabel}</Link>
          <ChevronRight size={12} />
          <span className="text-[#2b2620] font-medium">{item.keyword}</span>
        </nav>
      </div>

      <section className="container-tight pt-8 pb-14 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="chip mb-5">{parentLabel} · Toulouse</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-[#2b2620] tracking-tight leading-[1.05]">
            {item.h1}
          </h1>
          <p className="mt-6 text-lg text-[#5c554c] leading-relaxed">{item.intro}</p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <a
              href="https://www.resalib.fr/praticien/120570-jean-christophe-happillon-fasciatherapeute-quint-fonsegrives"
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
            >
              Réserver un bilan <ArrowRight size={16} />
            </a>
            <a href={`tel:${siteData.practitioner.phone.replace(/\s/g, "")}`} className="btn-outline">
              <Phone size={16} /> {siteData.practitioner.phone}
            </a>
          </div>
        </div>
        <div className="rounded-[2rem] overflow-hidden aspect-[4/3] lg:aspect-[4/5]">
          <img src={item.image} alt={item.h1} className="w-full h-full object-cover" loading="lazy" />
        </div>
      </section>

      {/* Content sections */}
      <section className="container-tight pb-16">
        <div className="max-w-3xl mx-auto space-y-10">
          {item.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2b2620] mb-4 leading-tight">{s.h2}</h2>
              <p className="text-[#5c554c] text-[17px] leading-[1.75]">{s.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Zones intervention */}
      <section className="bg-gradient-to-br from-[#f5ead9] to-[#fce8d5] py-16">
        <div className="container-tight max-w-3xl">
          <span className="chip mb-4"><MapPin size={12} /> Zones d'intervention</span>
          <h2 className="font-serif text-3xl font-medium text-[#2b2620] mb-4 leading-tight">
            {parentLabel} à Toulouse et alentours
          </h2>
          <p className="text-[#5c554c] leading-relaxed">{SERVICE_ZONES_TEXT}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <Link key={c.slug} to={cityUrl(c.slug)} className="px-3 py-1.5 rounded-full bg-white text-sm text-[#5c554c] border border-[#eee5d6] hover:border-[#c76d4b] hover:text-[#c76d4b] transition-colors">
                {c.short || c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {item.faq && item.faq.length > 0 && (
        <section className="container-tight py-16 max-w-3xl">
          <h2 className="font-serif text-3xl font-medium text-[#2b2620] mb-8">Questions fréquentes</h2>
          <div className="space-y-4">
            {item.faq.map((f, i) => (
              <details key={i} className="bg-white rounded-2xl border border-[#eee5d6] p-5 group">
                <summary className="font-serif text-lg font-medium text-[#2b2620] cursor-pointer list-none flex items-center justify-between">
                  {f.q}
                  <ChevronRight size={18} className="text-[#c76d4b] group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-4 text-[#5c554c] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Related specialties */}
      <section className="container-tight pb-16">
        <h2 className="font-serif text-3xl font-medium text-[#2b2620] mb-8">Autres accompagnements</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {otherSpecialties.map((s) => (
            <Link
              key={s.slug}
              to={service === "fasciatherapie" ? fasciaUrl(s.slug) : coachUrl(s.slug)}
              className="group bg-white rounded-3xl border border-[#eee5d6] overflow-hidden hover:border-[#c76d4b]/40 hover:shadow-lg transition-all"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={s.image} alt={s.h1} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-5">
                <h3 className="font-serif text-lg font-medium text-[#2b2620] group-hover:text-[#c76d4b] transition-colors leading-snug">{s.h1}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <RelatedArticles
        category={service === "fasciatherapie" ? "Fasciathérapie" : "Sport santé"}
        title="Articles liés"
        subtitle="Nos derniers conseils et retours d'expérience sur ce sujet."
      />
      <CTASection />
    </>
  );
}
